/**
 * VoiceMemoFAB — botón flotante 🎙️ siempre visible para cargar pedidos por voz.
 *
 * Estados:
 *  - idle: botón circular 56px abajo-derecha, icono micrófono
 *  - recording: botón rojo pulsante + bottom sheet con duración + 2 botones (cancelar / stop)
 *  - preview: bottom sheet con audio player + descartar / re-grabar / enviar
 *
 * El audio se sube a /api/portal/cliente/:id/pedidos con audio_base64 y audio_mime.
 * El backend transcribe automáticamente con Gemini y crea el pedido.
 *
 * NO se muestra en:
 *  - Login / verify / share público
 *  - Si el rol del cliente es "viewer"
 */

import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Mic, Square, X, Send, Trash2, RotateCw, Sparkles } from "lucide-react";
import { api, getClienteId } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useToast } from "./Toast.jsx";

const MAX_DURATION = 300; // v7.7.0 P8.b · 5 min cap
const WARN_DURATION = 240; // aviso a los 4 min

// v7.7.0 P6.4 · haptic feedback (no-op en desktop)
function haptic(pattern = 10) {
  if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(pattern);
}

export default function VoiceMemoFAB() {
  const { clienteActivo, clienteActivoId } = useAuth();
  const { toast } = useToast();
  // Pantallas de decisión (Revisión /tareas/aprobar y ficha /tareas/:id): su
  // barra de acciones (Cambios / Aprobar) vive abajo a la derecha, justo donde
  // flota el botón. En escritorio pasa abajo a la izquierda del contenido; en
  // celular se oculta (las acciones ocupan todo el ancho) y el pedido por voz
  // sigue disponible desde las demás zonas.
  const { pathname } = useLocation();
  const enDecision = /^\/tareas\/(aprobar|(?!pedidos$|campanas$|favoritos$)[^/]+)$/.test(pathname);
  const enChat = /^\/chat(\/|$)/.test(pathname);   // en el chat el botón Enviar queda justo debajo del micrófono flotante
  const [open, setOpen] = useState(false);          // bottom sheet abierto
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendStage, setSendStage] = useState(null); // "uploading" | null — la 2da etapa (transcripción) ya corre con el sheet cerrado, ver enviar()
  const [previewUrl, setPreviewUrl] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startRef = useRef(0);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Limpiar al cerrar
  function cleanup() {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setRecording(false);
    setAudioBlob(null);
    setDuration(0);
    setPreviewUrl(null);
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }

  useEffect(() => () => cleanup(), []);

  // Si llega a MAX_DURATION, frenar auto
  useEffect(() => {
    if (recording && duration >= MAX_DURATION) {
      stopRecording();
    }
  }, [recording, duration]);

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setAudioBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
        clearInterval(timerRef.current);
        stream.getTracks().forEach(t => t.stop());
      };
      recorderRef.current = rec;
      startRef.current = Date.now();
      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startRef.current) / 1000));
      }, 250);
      rec.start();
      setRecording(true);
      haptic(30);
    } catch (err) {
      toast.error("No pude acceder al micrófono. Verificá permisos en el navegador.");
    }
  }

  function stopRecording() {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
      haptic([15, 40, 15]);
    }
  }

  // v7.7.0 P6.1 · Long-press mic: press-to-talk (WhatsApp-style)
  // El botón principal permite tanto tap normal (abre sheet) como hold-to-record.
  const holdTimerRef = useRef(null);
  const holdActiveRef = useRef(false);
  function onFabPressStart(e) {
    e.preventDefault();
    holdActiveRef.current = false;
    holdTimerRef.current = setTimeout(() => {
      holdActiveRef.current = true;
      setOpen(true);
      startRecording();
    }, 500);
  }
  function onFabPressEnd() {
    clearTimeout(holdTimerRef.current);
    if (holdActiveRef.current) {
      stopRecording();
      holdActiveRef.current = false;
    }
  }

  function discardAndRestart() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setAudioBlob(null);
    setPreviewUrl(null);
    setDuration(0);
    startRecording();
  }

  function discardAll() {
    cleanup();
    setOpen(false);
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result).split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function enviar() {
    if (!audioBlob || !clienteActivoId) return;
    setSending(true);
    setSendStage("uploading");
    try {
      const base64 = await blobToBase64(audioBlob);
      await api.pedidoCrear(clienteActivoId, {
        titulo: "",            // el backend lo deriva de la transcripción
        descripcion: "",
        tipo_pedido: "nueva-tarea",
        urgencia: "media",
        audio_base64: base64,
        audio_mime: audioBlob.type,
      });
      // v8.5.65 · piel · la subida ya terminó acá, pero la transcripción sigue
      // corriendo async en el backend (el pedido aparece en otro lado como
      // "transcribiendo…") — el sheet se cierra en el mismo tick, así que un
      // segundo estado visual del FAB no llegaría a pintarse; este toast
      // previo al cierre es lo que comunica la segunda etapa.
      toast.success("Subido · transcribiendo en segundo plano…", { duration: 5000 });
      cleanup();
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "No pude enviar el audio");
    } finally {
      setSending(false);
      setSendStage(null);
    }
  }

  function formatTime(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  }

  // No mostrar si no hay cliente activo
  if (!clienteActivo || enChat) return null;

  return (
    <>
      {/* Botón flotante · v8.3.8 · un solo gesto (tap) — el long-press dual confundía */}
      {!open && (
        // v8.5.64 · piel · data-tour: hook para OnboardingTour.jsx, sin cambio visual
        <button
          onClick={() => { setOpen(true); startRecording(); }}
          aria-label="Grabar audio"
          data-tour="voice-fab"
          className={`fixed bottom-20 sm:bottom-6 z-[var(--z-action-bar)] ${enDecision ? "hidden sm:flex sm:left-[calc(var(--sidebar-w)+24px)]" : "right-4 sm:right-6"} w-14 h-14 rounded-full bg-[var(--ink)] text-[var(--surface)] shadow-lg hover:shadow-2xl hover:scale-110 active:scale-95 transition-all flex items-center justify-center group select-none`}
          style={{ marginBottom: "max(0px, env(safe-area-inset-bottom))" }}
        >
          <Mic className="h-6 w-6" strokeWidth={2.5}/>
          <span className={`absolute ${enDecision ? "left-full ml-3" : "right-full mr-3"} px-3 py-1.5 bg-[var(--ink)] text-[var(--surface)] text-xs font-semibold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none hidden sm:block`}>
            Grabar audio · click para empezar
          </span>
        </button>
      )}

      {/* Bottom sheet recording / preview */}
      {open && (
        <div className="fixed inset-0 z-[var(--z-sheet)] flex items-end sm:items-center justify-center" onClick={(e) => {
          // No cerrar tocando backdrop si está grabando (evita pérdida accidental)
          if (recording) return;
          if (e.target === e.currentTarget) discardAll();
        }}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"/>
          <div className="relative w-full sm:max-w-md bg-[var(--raised)] rounded-t-[32px] sm:rounded-[20px] shadow-2xl animate-slideUp"
               style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-12 h-1.5 bg-[var(--line-control)] rounded-full"/>
            </div>

            {recording && (
              <RecordingPanel duration={duration} maxDuration={MAX_DURATION}
                              onStop={stopRecording} onCancel={discardAll}
                              formatTime={formatTime}/>
            )}

            {!recording && audioBlob && (
              <PreviewPanel previewUrl={previewUrl} duration={duration} sending={sending} sendStage={sendStage}
                            onDiscard={discardAll} onRestart={discardAndRestart} onSend={enviar}
                            formatTime={formatTime}/>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function RecordingPanel({ duration, maxDuration, onStop, onCancel, formatTime }) {
  const remaining = maxDuration - duration;
  return (
    <div className="px-5 pt-3 pb-5 sm:pt-5 text-center">
      <div className="text-[13px] uppercase tracking-wider font-bold text-[var(--danger-ink)] mb-2 flex items-center justify-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[var(--danger)] animate-pulse"/>
        Grabando
      </div>
      <h2 className="text-3xl font-bold text-[var(--ink)] tabular-nums mb-4">
        {formatTime(duration)}
      </h2>

      {/* Wave-ish animation usando barras */}
      <div className="flex items-end justify-center gap-1 h-12 mb-5">
        {Array.from({ length: 24 }).map((_, i) => {
          const heights = [40, 60, 30, 80, 50, 70, 35, 90, 45, 65, 55, 75, 40, 60, 30, 80, 50, 70, 35, 90, 45, 65, 55, 75];
          const h = heights[(i + duration) % 24];
          return (
            <div
              key={i}
              className="w-1 rounded-full bg-[var(--ink)]"
              style={{ height: `${h}%`, transition: "height 0.15s" }}
            />
          );
        })}
      </div>

      <p className="text-xs text-[var(--muted)] mb-5">
        Contanos qué necesitás. Lo transcribimos y se lo pasamos al equipo.
        {remaining < 30 && (
          <span className="block mt-1 text-[var(--warning-ink)] font-semibold">
            {remaining}s antes del corte automático
          </span>
        )}
      </p>

      <div className="flex justify-center gap-3">
        <button
          onClick={onCancel}
          className="px-5 py-2.5 rounded-full bg-[var(--panel)] text-[var(--ink)] font-extrabold text-sm hover:bg-[var(--line)] flex items-center gap-1.5"
        >
          <X className="h-4 w-4"/> Cancelar
        </button>
        <button
          onClick={onStop}
          className="px-6 py-2.5 rounded-full bg-[var(--danger)] hover:opacity-90 text-white font-bold text-sm flex items-center gap-2 shadow-md"
        >
          <Square className="h-4 w-4" fill="white"/> Frenar
        </button>
      </div>
    </div>
  );
}

function PreviewPanel({ previewUrl, duration, sending, sendStage, onDiscard, onRestart, onSend, formatTime }) {
  return (
    <div className="px-5 pt-3 pb-5 sm:pt-5">
      <div className="text-[13px] uppercase tracking-wider font-bold text-primary mb-1 flex items-center gap-1.5">
        <Sparkles className="h-3 w-3"/> Tu mensaje grabado
      </div>
      <h2 className="text-base font-bold text-[var(--ink)] mb-1">
        {formatTime(duration)} de audio
      </h2>
      <p className="text-xs text-[var(--muted)] mb-4">
        Escuchalo. Si está bien, mandalo y lo procesamos en segundos.
      </p>

      {previewUrl && (
        <audio src={previewUrl} controls className="w-full mb-4 rounded-lg"/>
      )}

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={onDiscard}
          disabled={sending}
          className="px-3 py-2.5 rounded-2xl bg-[var(--panel)] text-[var(--ink)] font-extrabold text-xs hover:bg-[var(--line)] disabled:opacity-50 flex flex-col items-center gap-1"
        >
          <Trash2 className="h-4 w-4"/>
          <span>Descartar</span>
        </button>
        <button
          onClick={onRestart}
          disabled={sending}
          className="px-3 py-2.5 rounded-xl bg-[var(--warning-soft)] text-[var(--warning-ink)] font-semibold text-xs hover:opacity-90 disabled:opacity-50 flex flex-col items-center gap-1"
        >
          <RotateCw className="h-4 w-4"/>
          <span>Re-grabar</span>
        </button>
        <button
          onClick={onSend}
          disabled={sending}
          className="px-3 py-2.5 rounded-2xl bg-[var(--ink)] text-[var(--surface)] font-extrabold text-xs hover:opacity-90 disabled:opacity-50 flex flex-col items-center gap-1 shadow-md"
        >
          <Send className="h-4 w-4"/>
          <span>{sendStage === "uploading" ? "Subiendo…" : "Enviar"}</span>
        </button>
      </div>
    </div>
  );
}
